import { Suspense, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import {
  Drawer,
  Grid,
  Icon,
  IconButton,
  Layout,
  Logo,
  PageFallback,
  PageHeader,
} from '@bookora/ui';
import type { PageCrumb } from '@bookora/ui';
import { NavigationType, Outlet, useLocation, useNavigationType } from 'react-router-dom';

import { findSectionByPath } from '@/app/sections';
import { ActiveClinicLabel, ClinicSwitcher } from '@/auth/clinic-switcher';
import { IdleTimeout } from '@/auth/idle-timeout';
import { useAuth } from '@/auth/token-store';
import {
  ArmedNotice,
  GuidanceRoot,
  HelpMenu,
  HelpNavRow,
  SetupStepBar,
  tourAnchor,
} from '@/guidance';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { env } from '@/lib/env';

import { BillingBanner, BillingRefusalWatcher } from './billing-banner';
import { BranchSwitcher } from '@/features/branches/branch-switcher';
import { OfferModalHost } from '@/features/offers/offer-modal';
import { PlanRefusalWatcher } from '@/features/plan/plan-refusal-watcher';
import { AppBreadcrumbs } from './breadcrumbs';
import { NotificationBell } from './notification-bell';
import { PatientQuickSearch } from './patient-quick-search';
import { Sidebar } from './sidebar';
import { ThemeToggle } from './theme-toggle';
import { UserMenu } from './user-menu';

const { Sider, Header, Content } = Layout;

const SIDER_WIDTH = 264;
const SIDER_COLLAPSED_WIDTH = 72;
const COLLAPSE_STORAGE_KEY = 'bookora_clinic_sider_collapsed';

/**
 * The RTL application shell.
 *
 * ── LAYOUT ──────────────────────────────────────────────────────────────────
 *
 * The sider is on the RIGHT because the document is RTL — AntD's `Layout` puts
 * it there automatically under `direction="rtl"`, and every offset in this file
 * uses logical properties (`inset-inline-*`, `padding-inline-*`) so nothing has
 * to be flipped. Below the `md` breakpoint the sider becomes a drawer, because
 * a reception tablet in portrait has no room for both.
 *
 * ── WHAT LIVES HERE AND WHY ─────────────────────────────────────────────────
 *
 *  · The impersonation banner (SPEC §2.5) is the first thing in the tree and
 *    sticks above everything, including modals — a super-admin acting as a
 *    clinic user must never forget it.
 *  · `IdleTimeout` is mounted once for the whole authenticated area rather than
 *    per screen, so navigating does not reset the idle clock.
 *  · `BillingBanner` / `BillingRefusalWatcher` explain read-only mode (an
 *    ended trial, an unpaid invoice) on every screen at once, and turn the
 *    server's 402 into a way to pay. See `billing-banner.tsx`.
 *  · `PatientQuickSearch` puts the patient file one keystroke away from every
 *    screen — the desk's most frequent job.
 *  · `ArmedNotice` and `SetupStepBar` sit above the page: a context tour that
 *    is waiting for its place (with «لغو»), and, on a setup step's own page,
 *    how far setup is and the next step once this one is done.
 *  · The `<Suspense>` boundary wraps only the CONTENT, so a lazily-loaded page
 *    chunk arriving does not blank the sidebar, the bell or the clinic name.
 *  · `RouteView` gives each new page a short entrance and a fresh scroll
 *    position, and `RouteAnnouncer` tells a screen reader which page it is on
 *    — the two things a full page load did for free and a SPA does not.
 */
export function AppLayout() {
  const screens = Grid.useBreakpoint();
  const isCompact = !screens.md;
  const location = useLocation();
  const { activeClinic, impersonatedBy, canSwitchClinic } = useAuth();

  /*
   * `null` means the operator never chose. Then the width decides: below `xl`
   * the sider starts as the 72px rail, because a 264px sider on a 768–1199px
   * screen (a reception iPad, a small laptop) leaves the page ~470–900px and
   * every two- and three-up row in the panel collapses into slivers. An
   * explicit choice is remembered and always wins.
   */
  const [storedCollapsed, setStoredCollapsed] = useState<boolean | null>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
      return raw === null ? null : raw === '1';
    } catch {
      return null;
    }
  });
  // `useBreakpoint` is `{}` on the first render; treat unknown as wide so the
  // sider does not flash collapsed on a desktop.
  const collapsed = storedCollapsed ?? screens.xl === false;
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Crumbs a page's `PageHeader` hands up, so the shell draws ONE trail.
  const [pageCrumbs, setPageCrumbs] = useState<PageCrumb[] | null>(null);

  const section = findSectionByPath(location.pathname);
  useDocumentTitle(section?.label, activeClinic?.clinicName);

  // Route change closes the mobile drawer; leaving it open over the new screen
  // is the single most common mobile-nav bug.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  function toggleCollapsed(): void {
    const next = !collapsed;
    setStoredCollapsed(next);
    try {
      localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? '1' : '0');
    } catch {
      // Preference simply does not persist. Nothing else breaks.
    }
  }

  // The drawer is never "collapsed" — it only exists at compact widths, where
  // it is always the full-width nav.
  const brandCollapsed = collapsed && !isCompact;

  /*
   * The logo, not a letter. This used to be «ب» in a filled tile; the tile is
   * gone with it, because the new mark is artwork with its own silhouette and
   * `--bk-primary` is now the same charcoal the mark's ink is drawn in — the
   * tile would have painted it invisible.
   *
   * The wordmark also replaces the `env.appName` line that sat beside the old
   * mark: the lockup already says «بوکورا», and a header that says it twice
   * reads as a bug. The same goes for the clinic's name: it used to sit under
   * the lockup as well as in the header, so every screen said it twice. The
   * header keeps it, because there it is the clinic switcher — the copy that
   * does something.
   */
  const brand = (
    <div className={`bk-brand${brandCollapsed ? ' bk-brand--collapsed' : ''}`}>
      {/* Keyed by cut, so the swap remounts the artwork and `.bk-brand > *`
          fades the new one in instead of snapping between them. */}
      <Logo
        key={brandCollapsed ? 'mark' : 'lockup'}
        variant={brandCollapsed ? 'mark' : 'lockup'}
        size={brandCollapsed ? 30 : 32}
        title={env.appName}
      />
    </div>
  );

  return (
    <>
      {impersonatedBy ? (
        <div className="bk-impersonation-banner" role="status">
          <span className="bk-impersonation-banner__inner">
            <Icon name="shield" size={16} />
            <span>
              با حساب مدیر بوکورا، به‌جای این کاربر وارد شدید. همهٔ کارهای شما در گزارش فعالیت ثبت
              می‌شه.
            </span>
          </span>
        </div>
      ) : null}

      <a className="bk-skip-link" href="#bk-main">
        رفتن به محتوای اصلی
      </a>

      <Layout className="bk-shell" hasSider={!isCompact}>
        {isCompact ? (
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            // `start`, not `right`. Same edge in this RTL panel, but the panel
            // is not the only place this markup has to be correct — and the
            // kit no longer accepts a physical edge, which is the point.
            placement="start"
            width={SIDER_WIDTH}
            closable={false}
            styles={{ body: { padding: 0 } }}
            aria-label="منوی پنل"
          >
            {brand}
            {/*
              Which clinic, and as whom. On a phone the header has room for
              the clinic's name only, truncated; a person who works at two
              clinics needs to SEE which one they are about to book into
              before they open a screen, and switching belongs where the
              navigation is — a labelled action, not a bare glyph.
            */}
            {activeClinic ? (
              <div className="bk-drawer-clinic">
                <div className="bk-drawer-clinic__name">{activeClinic.clinicName}</div>
                {activeClinic.roleName ? (
                  <div className="bk-subtle bk-drawer-clinic__role">
                    نقش شما: {activeClinic.roleName}
                  </div>
                ) : null}
                {canSwitchClinic ? <ClinicSwitcher variant="block" /> : null}
                {/* Multi-branch: renders only when the clinic has more than one branch. */}
                <BranchSwitcher variant="block" />
              </div>
            ) : null}
            {/* «راهنما و پیشنهادها» — the compact home of the Help entry. */}
            <HelpNavRow onNavigate={() => setDrawerOpen(false)} />
            <Sidebar onNavigate={() => setDrawerOpen(false)} />
          </Drawer>
        ) : (
          <Sider
            {...tourAnchor('shell.nav')}
            className="bk-shell__sider"
            theme="light"
            width={SIDER_WIDTH}
            collapsedWidth={SIDER_COLLAPSED_WIDTH}
            collapsed={collapsed}
            trigger={null}
          >
            {brand}
            <Sidebar collapsed={collapsed} />
          </Sider>
        )}

        <Layout>
          <Header className="bk-shell__header">
            {isCompact ? (
              <IconButton
                {...tourAnchor('shell.menu-button')}
                type="text"
                name="menu"
                label="باز کردن منو"
                tooltipPlacement="bottom"
                onClick={() => setDrawerOpen(true)}
              />
            ) : (
              <IconButton
                type="text"
                // The chevron points at the edge the sider will move TOWARD:
                // `chevron-start` while it is open (it is about to fold back
                // against the start edge), `chevron-end` while it is closed.
                // AntD's own `MenuFold`/`MenuUnfold` pair is drawn for LTR and
                // was pointing into the content in this panel.
                name={collapsed ? 'chevron-end' : 'chevron-start'}
                // The label IS the bubble (kind `label`, 600 ms). A disclosure
                // is `aria-expanded` without anything covering it, so its
                // tooltip still opens while the sider is expanded.
                label={collapsed ? 'باز کردن منو' : 'جمع کردن منو'}
                tooltipPlacement="bottom"
                onClick={toggleCollapsed}
                aria-expanded={!collapsed}
              />
            )}

            <div className="bk-shell__context">
              {canSwitchClinic ? (
                <ClinicSwitcher variant={isCompact ? 'compact' : 'full'} />
              ) : (
                <ActiveClinicLabel />
              )}
              <BranchSwitcher variant={isCompact ? 'compact' : 'full'} />
            </div>

            <PatientQuickSearch compact={isCompact} />
            <NotificationBell />
            <ThemeToggle />
            {/* On compact the Help items live in the user menu and the nav drawer. */}
            {!isCompact && <HelpMenu />}
            <UserMenu />
          </Header>

          <BillingBanner />
          <OfferModalHost />

          <Content id="bk-main" className="bk-shell__content" tabIndex={-1}>
            <AppBreadcrumbs pageCrumbs={pageCrumbs} />
            {/* A context tour waiting for its place, and — on a setup step's own
                page — where the owner is in the setup and what comes next. */}
            <ArmedNotice />
            <SetupStepBar />
            <PageHeader.CrumbSlot value={setPageCrumbs}>
              <Suspense
                fallback={
                  <div className="bk-route-fallback">
                    <PageFallback />
                  </div>
                }
              >
                <RouteView>
                  <Outlet />
                </RouteView>
              </Suspense>
            </PageHeader.CrumbSlot>
          </Content>
          <RouteAnnouncer label={section?.label} />
        </Layout>
      </Layout>

      <IdleTimeout />
      <BillingRefusalWatcher />
      {/* 402 FEATURE_NOT_IN_PLAN — the plan lock family's notification. */}
      <PlanRefusalWatcher />
      {/* Welcome, tours, the hub and `?help=` deep links. Renders nothing itself. */}
      <GuidanceRoot />
    </>
  );
}

/**
 * The page's frame inside the content area.
 *
 * On every PATHNAME change it replays `.bk-route-view--enter` (a 6px rise and
 * fade on the base duration) and, for a forward navigation, returns the
 * document to the top. A query-string change — a filter, a page of a table —
 * is not a new page and does neither.
 *
 * The class is restarted on the same node rather than by re-keying the
 * wrapper: a key would remount the whole page subtree on every navigation and
 * throw away state a sibling route legitimately shares. It sits INSIDE the
 * Suspense boundary, so a lazily-loaded page runs its entrance when the chunk
 * lands rather than behind the skeleton.
 *
 * Back and forward (`POP`) keep the browser's own scroll restoration: a
 * receptionist returning to the patient list expects to land on the row she
 * left, not at the top of four hundred rows. A `REPLACE` does nothing at all:
 * it is an in-page address change, not a new page.
 */
function RouteView({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const ref = useRef<HTMLDivElement>(null);
  const previous = useRef(pathname);

  useLayoutEffect(() => {
    if (previous.current === pathname) return;
    previous.current = pathname;

    // A `replace` is the page rewriting its own address — closing a session
    // drawer (`/sessions/:id` → `/sessions`), picking a patient on the photos
    // screen, a redirect after a delete. The user did not go anywhere, so the
    // page neither replays its entrance nor jumps back to the top.
    if (navigationType === NavigationType.Replace) return;

    const node = ref.current;
    if (node) {
      node.classList.remove('bk-route-view--enter');
      // Reading a layout value flushes the removal, so re-adding the class
      // starts the animation again instead of being coalesced into a no-op.
      void node.offsetWidth;
      node.classList.add('bk-route-view--enter');
    }

    if (navigationType === NavigationType.Push) {
      try {
        window.scrollTo({ top: 0 });
      } catch {
        // Not every environment implements scrolling; the page still renders.
      }
    }
  }, [pathname, navigationType]);

  return (
    <div ref={ref} className="bk-route-view bk-route-view--enter">
      {children}
    </div>
  );
}

/**
 * A polite live region that names the page after each navigation.
 *
 * `document.title` changes, but no screen reader announces a title change in a
 * single-page app, so a keyboard user following a sidebar link heard nothing at
 * all. The first render is skipped: the page was just loaded and the reader
 * announces it on its own.
 */
function RouteAnnouncer({ label }: { label: string | undefined }) {
  const [message, setMessage] = useState('');
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setMessage(label ? `صفحهٔ ${label}` : '');
  }, [label]);

  return (
    <div className="bk-visually-hidden" role="status" aria-live="polite" aria-atomic="true">
      {message}
    </div>
  );
}
